import { useLogger } from "@bindery/logging/react";
import { cn } from "@bindery/ui/lib/utils";
import type { CSSProperties } from "react";
import { Link, useLocation } from "react-router";
import { useDesignPalette } from "@/components/design-switcher";

export const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/reader", label: "Library" },
  { href: "/stats", label: "Stats" },
] as const;

export function isActiveLink(href: string, pathname: string): boolean {
  return href === "/" ? pathname === "/" : pathname.startsWith(href);
}

export function NavLinks({
  orientation = "horizontal",
  onNavigate,
}: {
  orientation?: "horizontal" | "vertical";
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const log = useLogger();
  const palette = useDesignPalette();
  // The drawer is always dark glass, so only the capsule takes the palette.
  const usePalette = pathname === "/" && orientation !== "vertical";

  return (
    <nav
      aria-label="Main"
      className={cn(
        "flex gap-1",
        orientation === "vertical" ? "flex-col" : "flex-row items-center",
      )}
    >
      {NAV_LINKS.map((link) => {
        const active = isActiveLink(link.href, pathname);
        const paletteStyle: CSSProperties | undefined = usePalette
          ? active
            ? { backgroundColor: palette.activeBg, color: palette.activeText }
            : ({
                color: palette.textMuted,
                "--nav-link-hover-bg": palette.surfaceHover,
              } as CSSProperties)
          : undefined;
        return (
          <Link
            key={link.href}
            to={link.href}
            aria-current={active ? "page" : undefined}
            onClick={() => {
              log.event("web.navigation.click", {
                destination: link.href,
                source: orientation,
              });
              onNavigate?.();
            }}
            style={paletteStyle}
            className={cn(
              "rounded-xl px-3 text-[13px] font-semibold tracking-wide transition-all duration-150",
              orientation === "vertical"
                ? "flex h-16 items-center justify-center text-base"
                : "py-1.5",
              usePalette && !active && "hover:bg-[var(--nav-link-hover-bg)]",
              !usePalette &&
                (active
                  ? "bg-white/[0.12] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                  : "text-white/60 hover:bg-white/[0.06] hover:text-white/90"),
            )}
          >
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}
